'use client';
import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';
import Image from 'next/image';
import { Heading } from '@/components/ui/Heading';
import Link from 'next/link';
import { SwiperListProps } from '@/core/models/slider-model';
import { useDictionary } from '@/core/hooks/use-dictionary';
import CustomButton from './CustomButton';

export default function SwiperList({ data }: SwiperListProps) {
  const { dictionary } = useDictionary();
  const [expandedItem, setExpandedItem] = useState<any>(null);

  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(data.sliderItems)
      ? data.sliderItems
      : [];

  const toggleExpand = (item: any) => {
    setExpandedItem(
      expandedItem?.title === item.title ? null : item
    );
  };

  return (
    <div className="w-full col-span-12">
      <div className="flex justify-between items-center mb-10 max-md:flex-col max-md:items-start gap-4">
        <div>
          <Heading level={3}>{data.title}</Heading>
          <p className="text-primary-600">{data.description}</p>
        </div>
        {((data.link as string) || (data.url as string)) && (
          <CustomButton>
            <Link href={(data.link as string) || (data.url as string) || '#'}>
              {dictionary.ui?.pages.seeAll ?? 'See All'}
            </Link>
          </CustomButton>
        )}
      </div>

      <Swiper
        modules={[Pagination]}
        spaceBetween={16}
        pagination={{ clickable: true }}
        slidesPerView={1.8}
        breakpoints={{
          640: { slidesPerView: 2.3 },
          768: { slidesPerView: 3.3 },
          1024: { slidesPerView: 4.3 },
        }}
        className="pb-12!"
        style={{
          paddingBottom: "50px",
          "--swiper-bullet-width": "0.5rem",
          "--swiper-bullet-width-active": "1.5rem",
          "--swiper-bullet-height": "0.1rem",
          "--swiper-pagination-color": "#000",
          "--swiper-pagination-color-active": " #666666",
        } as React.CSSProperties}

      >
        {items.map((item, index) => {
          const isExpanded = expandedItem?.title === item.title;

          const Content = (
            <div className="relative overflow-hidden rounded-md">
              <div className="w-full h-full mb-2 relative aspect-9/16">
                {(item.image?.url || item.image_info?.url) && (
                  <Image
                    src={item.image?.url || item.image_info?.url}
                    alt={item.title || ''}
                    fill
                    className="object-cover rounded-md"
                  />
                )}
              </div>

              <div className="absolute inset-0 z-0 bg-linear-to-t rounded-md from-black/60 to-white/0" />

              <div className="absolute bottom-3 right-3 left-3 z-10">
                <Heading className="text-primary-0" level={4}>
                  {item.title}
                </Heading>

                {/* دکمه‌ی «بیشتر» */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item);
                  }}
                  className={`
                    mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs
                    bg-white/20 hover:bg-white/30 text-primary-0 backdrop-blur-sm
                    transition-all duration-200
                  `}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                  <span>{isExpanded ? 'کمتر' : 'بیشتر'}</span>
                </button>

                {/* Description */}
                <div
                  className={`
                    transition-all duration-300 ease-in-out
                    ${isExpanded ? 'max-h-20 opacity-100 mt-2' : 'max-h-0 opacity-0'}
                    overflow-hidden
                  `}
                >
                  <p className="text-primary-100 text-sm">{item.description}</p>
                </div>
              </div>
            </div>
          );

          return (
            <SwiperSlide key={index}>
              {item.link ? (
                <Link href={item.link}>{Content}</Link>
              ) : (
                Content
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div >
  );
}